import {
  AttributeElement,
  type CommandElement,
  findContainmentRelationship,
  getAttributeDescriptionSlashMode,
  type GraphNode,
  INFER_INSTANCE_COMMAND,
  INFER_INSTANCES_COMMAND,
  type Inferred,
  iterateNodeElements,
  type NodeElement,
  normalizeText,
  ORDINAL_COMMAND,
  type Relationship,
  sanitizeAttributeDescriptionSlash,
  type Scope,
} from "@packages/commons";
import {cloneDeep, filter} from "lodash";
import {v4 as uuid} from "uuid";
import {
  type Attribute,
  type CompilerContext,
  type ConclusionLevel,
  type DecisionTableV2,
  iterateExpressions,
  type OpmConclusion,
  type OpmDecisionTable,
} from "./Compiler";
import CompilerScope from "./CompilerScope";
import { getDeclaredType, parseDeclaredValue } from "./DeclaredValue";
import {
  addError,
  type AttributeExpression,
  type CommandExpression,
  type Expression,
  extractAttributeDescription,
  getGuidForDescription,
  type LiteralExpression,
  type Operator,
  parse,
  type ParseResult,
  RelationshipExpression,
} from "./Parser";
import {VALIDATE_COMMAND} from "@imminently/decisively-core";
import { COMPILER_ERROR_TYPES } from "@imminently/decisively-core";

export interface ExpressionsConvertResult {
  elements: NodeElement[];
  connector: Operator | undefined;
  expressionMap: WeakMap<NodeElement, Expression>;
  scopedEntity: string | undefined;
}

// get the entity contaiment chain up to the root
const getRelationshipChain = (relationships: Relationship[], entity: string): string[] => {
  const chain: string[] = [];
  let relationship = relationships.find((relationship) => relationship.name === entity);
  while (relationship) {
    if (relationship.name) {
      chain.push(relationship.name);
    }
    relationship = relationships.find(
      (other) => other.target === relationship?.source && other.id !== relationship?.id,
    );
  }
  return chain;
};

export const createAttributeNode = (context: CompilerContext, attribute: Attribute): GraphNode => {
  return {
    description: attribute.description,
    entity: attribute.entity,
    id: attribute.id,
    type: attribute.type ?? "auto",
    hidden: Boolean(attribute.entity && attribute.entity !== "global"),
    usedIn: context.currentDocument?.id ? [context.currentDocument?.id] : undefined,
  };
};

const INVALID_DECISION_TABLE_MESSAGE =
  "One of the decision tables is incomplete and could not be checked. Refresh the document and, if the issue remains, rebuild the affected decision table.";

const isObject = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const hasStringText = (value: unknown): value is Record<string, unknown> & { text: string } => {
  return isObject(value) && typeof value.text === "string";
};

const isValidDecisionTableCondition = (condition: unknown): boolean => {
  if (!isObject(condition)) return false;

  if (condition.type === "operator") {
    return Array.isArray(condition.children) && condition.children.every(isValidDecisionTableCondition);
  }

  return condition.type === "condition" && typeof condition.text === "string";
};

const isValidDecisionTableSubConclusion = (subConclusion: unknown): boolean => {
  if (!isObject(subConclusion)) return false;
  if (!hasStringText(subConclusion)) return false;

  return isValidDecisionTableCondition(subConclusion.conditions);
};

const isValidDecisionTable = (decisionTable: unknown): decisionTable is DecisionTableV2 => {
  if (!isObject(decisionTable)) return false;
  if (!hasStringText(decisionTable.header)) return false;
  if (!Array.isArray(decisionTable.subConclusions)) return false;
  if (decisionTable.otherwise !== undefined && !hasStringText(decisionTable.otherwise)) return false;

  return decisionTable.subConclusions.every(isValidDecisionTableSubConclusion);
};

const addInvalidDecisionTableError = (context: CompilerContext) => {
  addError(context.errors, {
    type: COMPILER_ERROR_TYPES.INVALID_DECISION_TABLE.id,
    message: INVALID_DECISION_TABLE_MESSAGE,
  });
};

export const getScopedRelationship = (
  relationships: Relationship[],
  from: string | Relationship | undefined,
  to: string | Relationship | undefined,
  expectGlobal?: boolean,
): { relationship: Relationship | undefined; reverse: boolean | undefined } => {
  if (!relationships) {
    return {relationship: undefined, reverse: undefined};
  }

  let toRelationship =
    typeof to === "string"
      ? relationships.find((relationship) => relationship.id === to || relationship.name === to)
      : to;

  if (!to) {
    return {relationship: undefined, reverse: undefined};
  }

  if (toRelationship?.containment && expectGlobal) {
    return {relationship: toRelationship, reverse: false};
  }

  //console.log(relationship, attributeAssignment?.attribute.entity);

  let reverse: boolean | undefined = toRelationship ? false : undefined;
  const fromRelationship =
    typeof from === "string"
      ? relationships.find(
        (relationship) => relationship.id === from || (relationship.containment && relationship.name === from),
      )
      : from;

  if (fromRelationship && toRelationship) {
    // Recursive function to traverse all possible paths upwards
    // This handles cases where there are multiple parent relationships
    const visited = new Set<string>();
    const findPathToTarget = (currentRelationship: Relationship): boolean => {
      if (visited.has(currentRelationship.id)) {
        return false;
      }
      visited.add(currentRelationship.id);

      // Find all parent relationships (not just the first one)
      const parentRelationships = relationships.filter(
        (other) => other.reverseText ? other.target === currentRelationship.target : other.target === currentRelationship.source && other.id !== currentRelationship.id,
      );

      for (const parentRelationship of parentRelationships) {
        if (parentRelationship.containment && parentRelationship.name === toRelationship?.name) {
          return true;
        }

        if (findPathToTarget(parentRelationship)) {
          return true;
        }
      }

      return false;
    };

    if (findPathToTarget(fromRelationship)) {
      reverse = true;
      toRelationship = fromRelationship;
    }
  }
  return {relationship: toRelationship, reverse: reverse};
};

const assumeConnectorAttributeTypes = (scope: CompilerScope, elements: NodeElement[]) => {
  for (const element of elements) {
    if (element.attributeId) {
      const attribute = scope.getAttributeById(element.attributeId);
      if (attribute) {
        if (!attribute.type || attribute.type === "auto") {
          attribute.type = "boolean";
        }
      }
    }
  }
  return elements;
};

export interface LevelConvertResult {
  connector: string | undefined;
  scopedEntity: string | undefined;
}

const convertLevelToElements = (
  context: CompilerContext,
  attributeAssignment: AttributeAssignment,
  level: ConclusionLevel,
  elements: NodeElement[],
  collectedOuts: Set<string>,
  connector: string | undefined,
  scope: CompilerScope,
  relationshipEntity?: string,
  scopedEntity?: string,
): LevelConvertResult => {
  const convertResult = convertExpressionsToElements(context, level.parseResult.scope, level.parseResult.expressions, {
    attributeAssignment,
    relationshipEntity,
    scopedEntity,
  });

  const [element] = convertResult.elements;
  if (!element) {
    return {connector, scopedEntity: convertResult.scopedEntity};
  }

  let childrenRelationshipEntity = relationshipEntity;

  if (attributeAssignment.inferred?.type === "targeted") {
    childrenRelationshipEntity = findContainmentRelationship(
      context.relationships,
      attributeAssignment.inferred.target,
    )?.name;
  } else {
    // @ts-ignore
    if (element.relationshipId) {
      const [expression] = level.parseResult.expressions;

      // has an alias
      if (expression.type === "command" && expression.arguments.some((arg) => arg.alias)) {
        childrenRelationshipEntity = "global";
      } else {
        const relationship = context.relationships.find(
          // @ts-ignore
          (relationship) => relationship.id === element.relationshipId,
        );
        if (relationship) {
          childrenRelationshipEntity = findContainmentRelationship(context.relationships, relationship.id)?.name;
        }
      }
    }
  }

  const childElements: NodeElement[] = [];
  let childConnector: string | undefined;
  for (const childLevel of level.children) {
    childConnector = convertLevelToElements(
      context,
      attributeAssignment,
      childLevel,
      childElements,
      collectedOuts,
      childConnector,
      scope,
      childrenRelationshipEntity,
      convertResult.scopedEntity,
    ).connector;
  }

  elements.push(element);
  //console.log('cr', convertResult, JSON.stringify(level.parseResult.expressions))
  if (!connector) {
    connector = convertResult.connector;
  }
  if (childElements.length) {
    (element as any).elements?.push(
      ...(childConnector && childConnector !== element.type
        ? [
          {
            type: childConnector,
            elements: assumeConnectorAttributeTypes(scope, childElements),
          },
        ]
        : element.type === "and" || element.type === "or"
          ? assumeConnectorAttributeTypes(scope, childElements)
          : childElements),
    );
  }

  for (const attribute of level.parseResult.scope.getCapturedAttributes()) {
    collectedOuts.add(attribute.id);
  }

  return {connector, scopedEntity: convertResult.scopedEntity};
};

const mergeNodeToGraphs = (context: CompilerContext, node: any, exists?: boolean) => {
  if (!node) {
    throw new Error("Node is undefined");
  }

  if (!node.identifier) {
    context.graph.setNode(node.id, node);
  }

  const foreign = exists && node.definedIn !== context.currentDocument?.id;
  context.subGraph.setNode(
    node.id,
    foreign
      ? {
        id: node.id,
        hidden: node.hidden,
        description: node.description,
        entity: node.entity,
        type: node.type,
        scope: node.scope,
        identifier: node.identifier,
        definedIn: node.definedIn,
      }
      : node,
  );

  if (node.definedIn && context.enableNamespaces) {
    // if there are also nodes with the same name defined in other documents, we need to mark them as dependent documents
    const others = filter(context.graph._nodes, (other) =>
      Boolean(
        other.id !== node.id &&
        other.definedIn &&
        other.description &&
        normalizeText(other.description) === normalizeText(node.description),
      ),
    );
    for (const other of others) {
      if (other.usedIn) {
        for (const usedIn of other.usedIn) {
          context.dependantDocuments.add(usedIn);
        }
      }
    }
  }
};

const resetEdgesInGraph = (context: CompilerContext, from: string, outs: Set<string>) => {
  const edges = context.graph.outEdges(from);
  if (edges) {
    for (const edge of edges) {
      context.graph.removeEdge(edge.v, edge.w);
    }
  }

  for (const out of outs) {
    addEdgeToGraph(context, from, out);
  }
};

const addEdgeToGraph = (context: CompilerContext, from: string, to: string) => {
  if (from === to) {
    return;
  }
  // slow, only do in tests
  if (process.env.NODE_ENV === "test") {
    if (!context.rootScope.getAttributeById(from)) {
      throw new Error(`Attribute '${from}' not found`);
    }
    if (!context.rootScope.getAttributeById(to)) {
      throw new Error(`Attribute '${to}' not found`);
    }
  }
  context.graph.setEdge(from, to);
  context.subGraph.setEdge(from, to);
  const targetInSubGraph = context.subGraph.node(to);
  if (targetInSubGraph) {
    const currentGraphValue = (context.subGraph.graph() || {}) as any;
    const usedAttributes = new Set<string>(currentGraphValue.usedAttributes || []);
    usedAttributes.add(to);
    context.subGraph.setGraph({
      ...currentGraphValue,
      usedAttributes: Array.from(usedAttributes),
    });
  }
};

export interface AttributeAssignment {
  attribute: Attribute;
  rightHandSide?: Expression;
  inferred?: Inferred;
  not?: boolean;
}

export const extractAttributeAssignment = (
  context: CompilerContext,
  scope: CompilerScope,
  expressions: Expression[],
): AttributeAssignment | undefined => {
  if (!expressions) {
    console.error("GraphBuilder", "extractAttributeAssignment called with no expression - likely bad parse");
    return undefined;
  }
  const firstExpression = expressions.find((expression) => expression.type !== "comment");
  let result: AttributeAssignment | undefined;

  if (firstExpression?.type === "attribute") {
    const attribute = scope.getAttributeById((firstExpression as AttributeExpression).attributeId);
    if (!attribute) {
      return undefined;
    }
    result = {
      attribute: attribute,
      rightHandSide: undefined,
      inferred: undefined,
      not: false,
    };
  } else if (firstExpression?.type === "command") {
    const commandExpression = firstExpression as CommandExpression;
    if (commandExpression.command.id === "not") {
      const attributeId = (commandExpression.arguments[0] as AttributeExpression).attributeId;
      if (!attributeId) {
        return undefined;
      }
      const attribute = scope.getAttributeById(attributeId);
      if (!attribute) {
        return undefined;
      }
      result = {
        attribute: attribute,
        rightHandSide: undefined,
        inferred: undefined,
        not: true,
      };
    } else if (commandExpression.command.id === "equals") {
      const subResult = extractAttributeAssignment(context, scope, [commandExpression.arguments[0]]);
      if (!subResult) {
        return undefined;
      }
      result = {
        attribute: subResult.attribute,
        rightHandSide: commandExpression.arguments[1],
        inferred: undefined,
        not: false,
      };
    } else if (commandExpression.command.id === INFER_INSTANCE_COMMAND.id) {
      const relationshipArgument = commandExpression.arguments[0];

      if (relationshipArgument?.type === "relationship") {
        const relationship = context.relationships.find(
          (relationship) => relationship.id === relationshipArgument.relationshipId,
        );

        if (relationship) {
          const sourceRelationship = context.relationships.find(
            (other) => other.target === relationship?.source && other.id !== relationship?.id,
          );
          let attribute = scope.getAttributeByDescription(relationship.name ?? relationship.text, context.namespace);
          if (!attribute) {
            const slashResult = sanitizeAttributeDescriptionSlash(
              relationship.name ?? relationship.text,
              getAttributeDescriptionSlashMode(context.disableGuids),
            );
            if (slashResult.error) {
              context.errors = addError(context.errors, slashResult.error);
              return undefined;
            }
            attribute = scope.addAttribute({
              id: getGuidForDescription(context, slashResult.description),
              description: slashResult.description,
              entity: sourceRelationship?.name ?? "global",
            });
          }

          const attributeAssignment = {
            not: false,
            attribute: attribute,
            rightHandSide: undefined,
            inferred: {
              type: "instanced",
              rel: relationship.id,
            },
          } satisfies AttributeAssignment;

          const initialResult = convertExpressionsToElements(context, scope, [commandExpression.arguments[1]], {
            attributeAssignment,
          });

          // @ts-ignore
          attributeAssignment.inferred.initial = initialResult.elements[0];

          result = attributeAssignment;
        }
      }
    } else if (commandExpression.command.id === "is-member-of") {
      const sourceExpression: any = commandExpression.arguments[0];
      const targetExpression = commandExpression.arguments[1];

      const sourceRelationship =
        sourceExpression.text &&
        context.relationships.find((relationship) => relationship.textSingular === sourceExpression.text);

      if (sourceRelationship && targetExpression?.type === "relationship") {
        const targetRelationship = context.relationships.find(
          (relationship) => relationship.id === targetExpression.relationshipId,
        );
        if (targetRelationship?.text) {
          const inferredEntity =
            context.relationships.find(
              (relationship) => relationship.containment && relationship.target === targetRelationship.source,
            )?.name ?? "global";
          let attribute = scope.getAttributeByDescription(targetRelationship.text, context.namespace);
          if (!attribute) {
            const slashResult = sanitizeAttributeDescriptionSlash(
              targetRelationship.text,
              getAttributeDescriptionSlashMode(context.disableGuids),
            );
            if (slashResult.error) {
              context.errors = addError(context.errors, slashResult.error);
              return undefined;
            }
            attribute = scope.addAttribute({
              id: getGuidForDescription(context, slashResult.description),
              description: slashResult.description,
              entity: inferredEntity,
            });
          } else {
            attribute.entity = inferredEntity;
          }
          result = {
            not: false,
            attribute: attribute,
            rightHandSide: undefined,
            inferred: {
              type: "targeted",
              rel: targetRelationship.id,
              target: sourceRelationship.id,
            },
          };
        }
      }
    } else if (commandExpression.command.id === "infer-instances-from-temporal") {
      const relationshipArgument = commandExpression.arguments[0];

      if (relationshipArgument?.type === "relationship") {
        const relationship = context.relationships.find(
          (relationship) => relationship.id === relationshipArgument.relationshipId,
        );

        if (relationship) {
          const sourceRelationship = context.relationships.find(
            (other) => other.target === relationship?.source && other.id !== relationship?.id,
          );
          let attribute = scope.getAttributeByDescription(relationship.name ?? relationship.text, context.namespace);
          if (!attribute) {
            const slashResult = sanitizeAttributeDescriptionSlash(
              relationship.name ?? relationship.text,
              getAttributeDescriptionSlashMode(context.disableGuids),
            );
            if (slashResult.error) {
              context.errors = addError(context.errors, slashResult.error);
              return undefined;
            }
            attribute = scope.addAttribute({
              id: getGuidForDescription(context, slashResult.description),
              description: slashResult.description,
              entity: sourceRelationship?.name ?? "global",
            });
          }

          const attributeAssignment = {
            not: false,
            attribute: attribute,
            rightHandSide: undefined,
            inferred: {
              type: "temporal",
              rel: relationship.id,
              startAttribute: undefined as any,
              endAttribute: undefined as any,
            },
          } satisfies AttributeAssignment;

          const initialResult = convertExpressionsToElements(context, scope, commandExpression.arguments.slice(1), {
            attributeAssignment,
          });

          const extractCorrectAttribute = (element: NodeElement) => {
            if (!element.attributeId) {
              context.errors = addError(context.errors, {
                type: COMPILER_ERROR_TYPES.INVALID_ARGUMENT.id,
                message: "Invalid argument for infer-instances-from-temporal",
              });
            }
            return element.attributeId as string;
          };

          attributeAssignment.inferred.startAttribute = extractCorrectAttribute(initialResult.elements[0]);
          attributeAssignment.inferred.endAttribute = extractCorrectAttribute(initialResult.elements[1]);

          result = attributeAssignment;
        }
      }
    } else if (commandExpression.command.id === "infer-instances-from-list") {
      const relationshipArgument = commandExpression.arguments[0];

      if (relationshipArgument?.type === "relationship") {
        const relationship = context.relationships.find(
          (relationship) => relationship.id === relationshipArgument.relationshipId,
        );

        if (relationship) {
          const sourceRelationship = context.relationships.find(
            (other) => other.target === relationship?.source && other.id !== relationship?.id,
          );
          let attribute = scope.getAttributeByDescription(relationship.name ?? relationship.text, context.namespace);
          if (!attribute) {
            const slashResult = sanitizeAttributeDescriptionSlash(
              relationship.name ?? relationship.text,
              getAttributeDescriptionSlashMode(context.disableGuids),
            );
            if (slashResult.error) {
              context.errors = addError(context.errors, slashResult.error);
              return undefined;
            }
            attribute = scope.addAttribute({
              id: getGuidForDescription(context, slashResult.description),
              description: slashResult.description,
              entity: sourceRelationship?.name ?? "global",
            });
          }

          const attributeAssignment = {
            not: false,
            attribute: attribute,
            rightHandSide: undefined,
            inferred: ({
              type: "list",
              rel: relationship.id,
              listAttribute: undefined as any,
              valueAttribute: undefined as any,
            }) satisfies Inferred,
          } satisfies AttributeAssignment;

          const initialResult = convertExpressionsToElements(context, scope, commandExpression.arguments.slice(1), {
            attributeAssignment,
          });

          const extractCorrectAttribute = (element: NodeElement) => {
            if (!element.attributeId) {
              context.errors = addError(context.errors, {
                type: COMPILER_ERROR_TYPES.INVALID_ARGUMENT.id,
                message: "Invalid argument for infer-instances-from-list",
              });
            }
            return element.attributeId as string;
          };

          attributeAssignment.inferred.listAttribute = extractCorrectAttribute(initialResult.elements[0]);
          attributeAssignment.inferred.valueAttribute = extractCorrectAttribute(initialResult.elements[1]);

          result = attributeAssignment;
        }
      }
    }
  }

  return result;
};

const mergeIdentifierToGraphs = (context: CompilerContext, attributes: CompilerScope, entity: string) => {
  const relationship = context.relationships.find((relationship) => relationship.name === entity);
  if (relationship?.textSingular) {
    const rawDescription = normalizeText(relationship.textSingular);
    const slashResult = sanitizeAttributeDescriptionSlash(rawDescription, getAttributeDescriptionSlashMode(context.disableGuids));
    if (slashResult.error) {
      context.errors = addError(context.errors, slashResult.error);
      return;
    }
    const description = slashResult.description;
    const existingAttribute = attributes.getAttributeByDescription(description, context.namespace);
    if (existingAttribute) {
      const existing = context.graph.node(existingAttribute.id);
      mergeNodeToGraphs(
        context,
        {
          // @ts-ignore
          id: getGuidForDescription(context, description),
          ...existing,
          description: description,
          entity: entity,
          identifier: true,
        },
        Boolean(existing),
      );
    }
  }
};

const flushScopeToGraphs = (context: CompilerContext, scope: CompilerScope) => {
  scope.mergeToParent();
  for (let attribute of scope.getCapturedAttributes()) {
    if (attribute.derivativeFrom) {
      const derivativeFrom = scope.getAttributeByDescription(attribute.derivativeFrom, context.namespace);
      if (!derivativeFrom) {
        throw new Error(`Attribute '${attribute.derivativeFrom}' not found`);
      }
      attribute = derivativeFrom;
    }
    let attributeNode: GraphNode | undefined = context.graph.node(attribute.id);
    if (!attributeNode) {
      attributeNode = createAttributeNode(context, attribute);
    }
    if (context.currentDocument?.id) {
      if (!attributeNode.usedIn) {
        attributeNode.usedIn = [context.currentDocument?.id];
      } else if (!attributeNode.usedIn.includes(context.currentDocument?.id)) {
        attributeNode.usedIn.push(context.currentDocument?.id);
      }
    }
    attributeNode.hidden = attributeNode.entity !== "global";
    mergeNodeToGraphs(context, attributeNode, true);

    if (attribute.entity !== "global") {
      mergeIdentifierToGraphs(context, scope, attribute.entity);
    }
  }
};

const checkDuplicateProven = (context: CompilerContext, attribute: Attribute): boolean => {
  if (attribute.definedIn) {
    context.errors = addError(context.errors, {
      type: COMPILER_ERROR_TYPES.DUPLICATE_PROVEN_ATTRIBUTE.id,
      message: `Attribute '${attribute.description}' is already defined${attribute.namespace ? ` in namespace '${attribute.namespace}'` : ""}`,
    });
    return true;
  }

  attribute.definedIn = context.currentDocument?.id;
  return false;
};

const updateNodeUsedRelationships = (node: GraphNode) => {
  const allRelationshipIds = new Set<string>();

  // Use iterateNodeElements to collect all relationship IDs
  iterateNodeElements(node, (element: NodeElement) => {
    if ((element as any).relationshipId && element.type !== "for") {
      allRelationshipIds.add((element as any).relationshipId);
    }
    return undefined;
  });

  // Update the node's usedRelationships array
  if (allRelationshipIds.size > 0) {
    node.usedRelationships = Array.from(allRelationshipIds);
  } else {
    node.usedRelationships = undefined;
  }
};

export default class GraphBuilder {
  private currentConclusionParseResult: ParseResult | undefined;
  private levels: Record<number, ConclusionLevel[]> = {};
  private currentEntity: string | undefined;

  getCurrentScope() {
    return this.currentConclusionParseResult?.scope;
  }

  discardPreviousConclusion() {
    this.currentConclusionParseResult = undefined;
    this.levels = {};
    this.currentEntity = undefined;
  }

  flushContactStartOrEndToGraph(context: CompilerContext, parseResult: ParseResult, isStart: boolean) {
    const commandExpression = parseResult.expressions[0] as CommandExpression;
    const referencedAttributeArgument = commandExpression.arguments[0];

    const referencedAttribute = parseResult.scope.getAttributeById(
      (referencedAttributeArgument as AttributeExpression).attributeId,
    );

    if (!referencedAttribute) {
      context.errors = addError(context.errors, {
        type: COMPILER_ERROR_TYPES.INVALID_CONTACT_METADATA.id,
        message: "Could not find referenced attribute",
      });
      return;
    }

    const id = uuid();

    const attribute = parseResult.scope.addAttribute({
      id,
      entity: referencedAttribute.entity,
      definedIn: context.currentDocument?.id,
      description: undefined,
      type: "boolean",
    });

    let attributeNode: GraphNode = context.graph.node(id);
    if (!attributeNode) {
      attributeNode = createAttributeNode(context, attribute);
    }
    attributeNode.definedIn = context.currentDocument?.id;

    const collectedOuts = new Set<string>();
    collectedOuts.add(referencedAttribute.id);
    attributeNode.value = {
      type: "attribute",
      attributeId: referencedAttribute.id,
    };
    // @ts-ignore
    attributeNode[isStart ? "contactStart" : "contactEnd"] = true;
    attributeNode.meta = true;

    flushScopeToGraphs(context, parseResult.scope);
    mergeNodeToGraphs(context, attributeNode, false);
    resetEdgesInGraph(context, attributeNode.id, collectedOuts);
  }

  flushValidationToGraph(context: CompilerContext, parseResult: ParseResult) {
    const commandExpression = parseResult.expressions[0] as CommandExpression;
    const message = (commandExpression.arguments[0] as LiteralExpression).value;
    const possibleEntityName = (commandExpression.arguments[1] as AttributeExpression)?.text;

    const command = commandExpression.command;

    const id = uuid();

    let entity = "global";

    // we need to pre-pass the conditions to figure out the entity
    /*const childrenEntities = new Set<string>();
    for (const level of Object.values(this.levels).flat(1)) {
      iterateExpressions(level.parseResult.expressions, (expression) => {
        if (expression.type === "attribute") {
          const attribute = parseResult.scope.getAttributeById((expression as AttributeExpression).attributeId);
          if (attribute && attribute.entity !== "global") {
            childrenEntities.add(attribute.entity);
          }
        }
      });
    }

    if (childrenEntities.size === 1) {
      entity = childrenEntities.values().next().value as string;
    } else {
      // we need to find the 'lowest' entity in the hierarchy
      // if there are entities in different hierarchies, we need to error
      let currentEntity: string | undefined = undefined;

      for (const entity of childrenEntities) {
        if (!currentEntity) {
          currentEntity = entity;
        } else {
          const chain = getRelationshipChain(context.relationships, entity);
          const indexOfCurrent = chain.indexOf(currentEntity);
          const indexOfEntity = chain.indexOf(entity);
          if (indexOfCurrent === -1 || indexOfEntity === -1) {
            const currentChain = getRelationshipChain(context.relationships, currentEntity);

            if (!intersection(chain, currentChain).length) {
              context.errors = addError(context.errors, {
                type: COMPILER_ERROR_TYPES.INVALID_SCOPE.id,
                message: `Entities '${currentEntity}' and '${entity}' are not related but both used in validation`,
              });
              return;
            }
          }

          if (indexOfEntity < indexOfCurrent) {
            currentEntity = entity;
          }
        }
      }

      if (currentEntity) {
        entity = currentEntity;
      }
    }*/

    const relationship = context.relationships.find(
      (relationship) => relationship.textSingular === possibleEntityName,
    );
    if (relationship?.name) {
      entity = relationship.name;
    }

    const attribute = parseResult.scope.addAttribute({
      id,
      entity: entity,
      definedIn: context.currentDocument?.id,
      description: undefined,
      type: "boolean",
    });

    let attributeNode: GraphNode = context.graph.node(id);
    if (!attributeNode) {
      attributeNode = createAttributeNode(context, attribute);
    }
    attributeNode.definedIn = context.currentDocument?.id;

    const collectedOuts = new Set<string>();
    const conditions = this.flushConditionsToElements(
      context,
      parseResult.scope,
      {
        attribute: attribute,
        rightHandSide: undefined,
        inferred: undefined,
        not: false,
      },
      collectedOuts,
    );

    attributeNode.meta = true;
    attributeNode.conditions = conditions;
    attributeNode.validation = {
      message: message,
      severity: command.id === VALIDATE_COMMAND.id ? "error" : "warning",
    };
    attributeNode.value = {
      value: true,
      type: "value",
    };
    attributeNode.otherwise = {
      value: false,
      type: "value",
    };

    flushScopeToGraphs(context, parseResult.scope);
    mergeNodeToGraphs(context, attributeNode, false);
    resetEdgesInGraph(context, attributeNode.id, collectedOuts);
  }

  private flushConditionsToElements(
    context: CompilerContext,
    scope: CompilerScope,
    attributeAssignment: AttributeAssignment,
    collectedOuts: Set<string>,
  ): NodeElement[] {
    const topLevelConditions = this.levels[1];
    let connector: string | undefined;
    const elements: NodeElement[] = [];
    if (topLevelConditions) {
      for (const level of topLevelConditions) {
        connector = convertLevelToElements(
          context,
          attributeAssignment,
          level,
          elements,
          collectedOuts,
          connector,
          scope,
        ).connector;
      }
    }

    //console.log('e', elements, topLevelConditions, connector, JSON.stringify(topLevelConditions, null, 2))
    this.levels = {};
    this.currentEntity = undefined;

    collectedOuts.delete(attributeAssignment.attribute.id);
    return connector
      ? [
        {
          type: connector,
          elements: assumeConnectorAttributeTypes(scope, elements),
        },
      ]
      : elements;
  }

  flushConclusionToGraph(context: CompilerContext, parseResult: ParseResult): AttributeAssignment | undefined {
    if (parseResult.errors.length) {
      throw new Error("Trying to flush conclusion with errors");
    }

    const attributeAssignment = extractAttributeAssignment(context, parseResult.scope, parseResult.expressions);
    if (attributeAssignment) {
      // we are trying to assign to an attribute that is actually assigned in a different namespace, make a new attr instead
      if (
        attributeAssignment.attribute.namespace &&
        context.namespace &&
        attributeAssignment.attribute.namespace !== context.namespace
      ) {
        const scope = parseResult.scope;
        const oldAttribute = attributeAssignment.attribute;
        const newAttribute = scope.addAttribute({
          ...cloneDeep(oldAttribute),
          definedIn: undefined,
          id: uuid(),
          namespace: context.namespace,
        });
        attributeAssignment.attribute = newAttribute;
        iterateExpressions(parseResult.expressions, (expression: any) => {
          if (expression.attributeId === oldAttribute.id) {
            expression.attributeId = newAttribute.id;
          }
        });
        scope.removeAttributeById(oldAttribute.id);
      }

      if (checkDuplicateProven(context, attributeAssignment.attribute)) {
        return;
      }

      attributeAssignment.attribute.definedIn = context.currentDocument?.id;

      let attributeNode: GraphNode = context.graph.node(attributeAssignment.attribute.id);
      if (!attributeNode) {
        attributeNode = createAttributeNode(context, attributeAssignment.attribute);
      }

      attributeNode.value = undefined;
      attributeNode.conditions = undefined;
      attributeNode.entity = attributeAssignment.attribute.entity;
      attributeNode.otherwise = undefined;
      attributeNode.rows = undefined;

      const collectedOuts = new Set<string>();
      attributeNode.inferred = attributeAssignment.inferred;

      if (attributeNode.inferred) {
        let entity = "global";
        const relationship = context.relationships.find(
          (relationship) => relationship.id === attributeNode.inferred?.rel,
        );
        if (relationship) {
          const sourceRelationship = context.relationships.find(
            (other) => other.target === relationship?.source && other.id !== relationship?.id && other.name,
          );
          if (sourceRelationship?.name) {
            entity = sourceRelationship.name;
          }
        }

        if (!attributeNode.identifier) {
          attributeNode.entity = entity;
        }

        if (attributeNode.inferred.type === "temporal") {
          const startAttribute = parseResult.scope.getAttributeById(attributeNode.inferred.startAttribute);
          if (startAttribute) {
            this.markNodeConcluded(context, startAttribute);
          }

          const endAttribute = parseResult.scope.getAttributeById(attributeNode.inferred.endAttribute);
          if (endAttribute) {
            this.markNodeConcluded(context, endAttribute);
          }
        }
      }

      const conditions = this.flushConditionsToElements(context, parseResult.scope, attributeAssignment, collectedOuts);
      if (conditions.length) {
        if (attributeNode.inferred) {
          attributeNode.inferred.conditions = conditions;
        } else {
          attributeNode.conditions = conditions;
        }
      }

      if (attributeAssignment.rightHandSide) {
        const elementsParse = convertExpressionsToElements(
          context,
          parseResult.scope,
          [attributeAssignment.rightHandSide],
          {attributeAssignment: attributeAssignment, declaredValueFor: attributeAssignment.attribute},
        );

        attributeNode.value = elementsParse.elements[0];
      }

      if (!attributeNode.value && conditions.length === 0 && !attributeNode.inferred && !attributeNode.identifier) {
        attributeNode.value = {value: true, type: "value"};
        attributeNode.otherwise = {value: false, type: "value"};
      }

      const collectedAttrs = parseResult.scope.getCapturedAttributes();
      //delete rightHandAttributes[attributeAssignment.attribute.description];

      if (collectedAttrs.length > 0) {
        // The equal in this conclusion uses other attributes (eg: attr1 = attr2 + 4)
        // We need to add these attributes to the graph, and link them
        // If the attributes list is empty this means we are comparing to a literal (eg: attr1 = 123)
        // In this case we have no attributes to link
        for (const attribute of collectedAttrs) {
          if (attribute.id !== attributeNode.id) {
            collectedOuts.add(attribute.id);
          }
        }
      }

      /*if (!attributeAssignment.rightHandSide && conditions.length === 0) {
          return undefined;
        }*/

      if (attributeNode.conditions?.length) {
        if (!attributeNode.value) {
          Object.assign(attributeNode, {
            type: attributeNode.type ?? "boolean",
            value: {value: true, type: "value"},
            otherwise: {value: false, type: "value"},
          });
        }

        // @ts-ignore
        if (!attributeNode.otherwise && attributeNode.value?.value === true) {
          attributeNode.otherwise = {value: false, type: "value"};
        }
      }

      // @ts-ignore
      if (!attributeNode.type && attributeNode.value?.type === "value") {
        // @ts-ignore
        if (typeof attributeNode.value.value === "boolean") {
          attributeNode.type = "boolean";
        }
      }

      if (attributeNode.conditions || attributeNode.rows || attributeNode.value || attributeNode.inferred) {
        this.markNodeConcluded(context, attributeAssignment.attribute, attributeNode);
      }

      if (attributeAssignment?.not) {
        // flip value & otherwise
        const value = attributeNode.value;
        attributeNode.value = attributeNode.otherwise;
        attributeNode.otherwise = value;
      }

      flushScopeToGraphs(context, parseResult.scope);
      mergeNodeToGraphs(context, attributeNode, Boolean(attributeNode));
      if (attributeNode.value?.type !== ORDINAL_COMMAND.id) {
        resetEdgesInGraph(context, attributeNode.id, collectedOuts);
      }

      updateNodeUsedRelationships(attributeNode);
    } else {
      flushScopeToGraphs(context, parseResult.scope);
      //console.error("No assignable attribute", parseResult);
    }

    return attributeAssignment;
  }

  flushPreviousConclusionToGraph(context: CompilerContext, newParseResult?: ParseResult) {
    const parseResult = this.currentConclusionParseResult;

    if (parseResult) {
      this.flushConclusionToGraph(context, parseResult);
    }

    this.currentConclusionParseResult = newParseResult;
    this.levels = {};
    this.currentEntity = undefined;
  }

  private addLevel(level: ConclusionLevel): ConclusionLevel {
    if (!this.levels[level.depth]) {
      this.levels[level.depth] = [];
    }
    this.levels[level.depth].push(level);
    return level;
  }

  markNodeConcluded(context: CompilerContext, attribute: Attribute, attributeNode?: GraphNode): GraphNode {
    let resolvedAttributeNode: GraphNode = attributeNode ?? context.graph.node(attribute.id);
    if (!resolvedAttributeNode) {
      resolvedAttributeNode = createAttributeNode(context, attribute);
    }

    resolvedAttributeNode.definedIn = context.currentDocument?.id;
    const sectionId = attribute.meta?.sectionId;
    resolvedAttributeNode.sectionId = sectionId === "global" ? undefined : sectionId;

    return resolvedAttributeNode;
  }

  addCondition(parseResult: ParseResult, depth: number) {
    if (parseResult.errors.length) {
      throw new Error("Trying to add condition with errors");
    }

    const level = this.addLevel({
      depth: depth,
      parseResult: parseResult,
      children: [],
    });

    const parentLevel = this.levels[depth - 1]?.[this.levels[depth - 1].length - 1];
    if (parentLevel) {
      parentLevel.children.push(level);
    }

    parseResult.scope.mergeToParent();
  }

  flushDecisionTableToGraphV1(context: CompilerContext, decisionTable: OpmDecisionTable) {
    const description = extractAttributeDescription(context, decisionTable.conclusion);
    const scope = new CompilerScope(`decisionTable[${description}]`, context.rootScope);

    const entity = context.matchEntity(description);

    const slashResult = sanitizeAttributeDescriptionSlash(description, getAttributeDescriptionSlashMode(context.disableGuids));
    if (slashResult.error) {
      context.errors = addError(context.errors, slashResult.error);
      return;
    }
    const safeDescription = slashResult.description;

    let attribute = scope.getAttributeByDescription(safeDescription, context.namespace);
    if (!attribute) {
      scope.addAttribute(
        (attribute = {
          id: getGuidForDescription(context, safeDescription),
          description: safeDescription,
          entity: entity || "global",
          generated: true,
        }),
      );
      //console.log(context.attributes);
    }

    if (checkDuplicateProven(context, attribute)) {
      return;
    }

    type GraphNodeWithRows = GraphNode & {
      value: NodeElement[];
      rows: Array<NodeElement[]>;
    };

    let attributeNode = context.graph.node(attribute.id) as GraphNodeWithRows;
    if (!attributeNode) {
      attributeNode = createAttributeNode(context, attribute) as GraphNodeWithRows;
    } else {
      attributeNode = {...attributeNode};
    }

    const collectedOuts = new Set<string>();
    Object.assign(attributeNode, {
      value: [],
      rows: [],
      definedIn: context.currentDocument?.id,
      conditions: undefined,
    });
    //console.log(attributeNode);

    if (decisionTable.sub_conclusions) {
      outer: for (const subConclusion of decisionTable.sub_conclusions) {
        const parseTokensResult = parse({
          input: subConclusion.text,
          context,
          scope: scope,
        });
        const elementsParse = convertExpressionsToElements(context, scope, parseTokensResult.expressions, {
          attributeAssignment: {
            attribute,
            rightHandSide: undefined,
            inferred: undefined,
            not: false,
          },
        });
        if (parseTokensResult.errors.length) {
          context.errors.push(...parseTokensResult.errors);
          return;
        }

        attributeNode.value.push(...elementsParse.elements);

        for (const attribute of parseTokensResult.scope.getCapturedAttributes()) {
          collectedOuts.add(attribute.id);
        }

        for (const declaration of subConclusion.conditions) {
          if (declaration.type.startsWith("OPM-level")) {
            const result = parse({
              input: (declaration as OpmConclusion).text,
              context,
              scope: scope,
            });
            if (result.errors.length) {
              context.errors.push(...result.errors);
              return;
            }

            const level = Number.parseInt(declaration.type.replace("OPM-level", ""));
            this.addCondition(result, level);
          } else if (declaration.type === "OPM-Alternativeconclusion") {
            attributeNode.rows.push([
              {
                type: "empty",
              },
            ]);

            continue outer;
          }
        }

        attributeNode.rows.push(
          this.flushConditionsToElements(
            context,
            scope,
            {
              attribute,
              rightHandSide: undefined,
              inferred: undefined,
              not: false,
            },
            collectedOuts,
          ),
        );
      }
    }

    flushScopeToGraphs(context, scope);
    mergeNodeToGraphs(context, attributeNode, Boolean(attributeNode));
    resetEdgesInGraph(context, attributeNode.id, collectedOuts);
  }

  flushDecisionTableToGraphV2(context: CompilerContext, decisionTable: DecisionTableV2) {
    if (!isValidDecisionTable(decisionTable)) {
      addInvalidDecisionTableError(context);
      return;
    }

    const scope = new CompilerScope(`decisionTable[${decisionTable.header.text}]`, context.rootScope);
    const parsedHeader = parse({
      input: decisionTable.header.text,
      context,
      scope: new CompilerScope(`decisionTable[${decisionTable.header.text}]`, context.rootScope),
    });
    let description: string | undefined;
    let inferred: Inferred | undefined;
    let entity: string | undefined;

    if (parsedHeader.expressions[0]?.type === "command") {
      if (parsedHeader.expressions[0].command.id !== INFER_INSTANCES_COMMAND.id) {
        addError(context.errors, {
          type: COMPILER_ERROR_TYPES.INVALID_SCOPE.id,
          message: `Invalid decision table header: ${decisionTable.header.text}`,
        });
        return;
      }

      const commandExpression = parsedHeader.expressions[0] as CommandExpression;
      const attributeExpression = commandExpression.arguments[1] as AttributeExpression;
      const relationshipExpression = commandExpression.arguments[0] as RelationshipExpression;

      const relationship = relationshipExpression?.relationshipId ? context.relationships.find(
        (relationship) => relationship.id === relationshipExpression.relationshipId,
      ) : undefined;


      if (!relationship) {
        addError(context.errors, {
          type: COMPILER_ERROR_TYPES.INVALID_SCOPE.id,
          message: `Invalid decision table header (no relationship provided): ${decisionTable.header.text}`,
        });
        return;
      }

      const containmentRelationship = findContainmentRelationship(context.relationships, relationship.id);
      if (!containmentRelationship) {
        addError(context.errors, {
          type: COMPILER_ERROR_TYPES.INVALID_SCOPE.id,
          message: `Invalid decision table header (no relationship provided): ${decisionTable.header.text}`,
        });
        return;
      }

      if (!attributeExpression?.attributeId) {
        addError(context.errors, {
          type: COMPILER_ERROR_TYPES.INVALID_SCOPE.id,
          message: `Invalid decision table header (no attribute provided): ${decisionTable.header.text}`,
        });
        return;
      }

      description = relationship?.text;
      inferred = {
        type: "table",
        rel: containmentRelationship.id,
        attribute: attributeExpression.attributeId,
      };

      const targetRelationship = context.relationships.find(
        (other) => other.target === relationship?.source && other.id !== relationship?.id && other.name,
      );
      if (targetRelationship?.name) {
        entity = targetRelationship.name;
      }
    } else {
      description = extractAttributeDescription(context, decisionTable.header.text);
      entity = context.matchEntity(description);

    }

    if (!description) {
      addError(context.errors, {
        type: COMPILER_ERROR_TYPES.INVALID_SCOPE.id,
        message: `Invalid decision table header: ${decisionTable.header.text}`,
      });
      return;
    }

    const slashResult = sanitizeAttributeDescriptionSlash(description, getAttributeDescriptionSlashMode(context.disableGuids));
    if (slashResult.error) {
      context.errors = addError(context.errors, slashResult.error);
      return;
    }
    description = slashResult.description;

    let attribute = scope.getAttributeByDescription(description, context.namespace);
    if (!attribute) {
      scope.addAttribute(
        (attribute = {
          id: getGuidForDescription(context, description),
          description: description,
          entity: entity || "global",
          generated: true,
        }),
      );
      //console.log(context.attributes);
    }

    if (checkDuplicateProven(context, attribute)) {
      return;
    }

    //console.log(context.entityRegexs, description);


    const collectedOuts = new Set<string>();
    const value: NodeElement[] = [];
    const rows: Array<NodeElement[]> = [];

    //console.log(attributeNode);

    for (const subConclusion of decisionTable.subConclusions) {
      const parseTokensResult = parse({
        input: subConclusion.text,
        context,
        scope,
      });
      const elementsParse = convertExpressionsToElements(context, scope, parseTokensResult.expressions, {
        attributeAssignment: {
          attribute,
          rightHandSide: undefined,
          inferred: undefined,
          not: false,
        },
        declaredValueFor: attribute,
      });
      if (parseTokensResult.errors.length) {
        context.errors.push(...parseTokensResult.errors);
        return;
      }
      if (elementsParse.elements.length > 1) {
        addError(context.errors, {
          type: COMPILER_ERROR_TYPES.INTERNAL_ERROR.id,
          message: `Cannot parse condition into multiple elements`,
        });
        return;
      }
      value.push(...elementsParse.elements);
      for (const attribute of parseTokensResult.scope.getCapturedAttributes()) {
        collectedOuts.add(attribute.id);
      }

      const process = (declaration: any, level = 1): boolean | undefined => {
        if (declaration.type === "operator") {
          const result = parse({
            input: declaration.operator === "and" ? "all" : "any",
            context,
            scope: scope,
          });
          if (result.errors.length) {
            context.errors.push(...result.errors);
            return false;
          }
          this.addCondition(result, level);
          for (const child of declaration.children) {
            if (process(child, level + 1) === false) {
              return false;
            }
          }
        } else if (declaration.type === "condition") {
          const result = parse({
            input: declaration.text,
            context,
            scope: scope,
          });
          if (result.errors.length) {
            context.errors.push(...result.errors);
            return false;
          }

          this.addCondition(result, level);
        }
      };
      //for (const declaration of subConclusion.conditions) {
      if (process(subConclusion.conditions, 1) === false) {
        return;
      }
      //}

      rows.push(
        this.flushConditionsToElements(
          context,
          scope,
          {
            attribute,
            rightHandSide: undefined,
            inferred: undefined,
            not: false,
          },
          collectedOuts,
        ),
      );
    }
    if (!inferred && decisionTable.otherwise) {
      const parseTokensResult = parse({
        input: decisionTable.otherwise.text,
        context,
        scope,
      });
      if (parseTokensResult.errors.length) {
        context.errors.push(...parseTokensResult.errors);
        return;
      }
      const elementsParse = convertExpressionsToElements(context, scope, parseTokensResult.expressions, {
        attributeAssignment: {
          attribute,
          rightHandSide: undefined,
          inferred: undefined,
          not: false,
        },
        declaredValueFor: attribute,
      });
      const firstElement = elementsParse.elements?.[0] as CommandElement;
      if (!firstElement) {
        // invalid decision table
        return;
      }

      if (firstElement?.elements?.length === 0) {
        // The value array is not meant to have empty elements. This can occur if the empty is 'uncertain' the system will think it should have elements (as part of an arg for uncertain())
        (firstElement as any).elements = undefined;
      }

      // Add any attributes that were extracted in the otherwise value column
      for (const attribute of parseTokensResult.scope.getCapturedAttributes()) {
        //console.log(parseTokensResult.scope.getCapturedAttributes(), parseTokensResult.expressions);
        collectedOuts.add(attribute.id);
      }
      if (elementsParse.elements.length > 1) {
        addError(context.errors, {
          type: COMPILER_ERROR_TYPES.INTERNAL_ERROR.id,
          message: `Cannot parse condition into multiple elements`,
        });
        return;
      }
      value.push(...elementsParse.elements);

      rows.push([
        {
          type: "empty",
        },
      ]);
      //continue outer;
    }
    let attributeNode: GraphNode = context.graph.node(attribute.id);
    if (!attributeNode) {
      attributeNode = createAttributeNode(context, attribute);
    } else {
      attributeNode = {...attributeNode};
    }

    Object.assign(attributeNode, {
      value: value,
      rows: rows,
      otherwise: undefined,
      conditions: undefined,
      definedIn: context.currentDocument?.id,
      entity,
      inferred,
    });

    flushScopeToGraphs(context, scope);
    mergeNodeToGraphs(context, attributeNode, Boolean(attributeNode));
    resetEdgesInGraph(context, attributeNode.id, collectedOuts);
  }
}

export interface ExpressionConvertContext {
  attributeAssignment?: AttributeAssignment;
  /**
   * The attribute a static value (literal) is compared with or assigned to. When it has a declared type, the literal
   * is checked against that type and converted to it, using the same rules as rule sheets.
   */
  declaredValueFor?: Attribute;
  inFor?: boolean;
  relationshipEntity?: string;
  scopedEntity?: string;
  expressionMap?: WeakMap<NodeElement, Expression>;
}

const COMPARISON_COMMANDS = new Set(["equals", "not-equals", "greater-than", "greater-than-equals", "less-than", "less-than-equals"]);
const ORDERING_SYMBOLS: Record<string, string> = {
  "greater-than": ">",
  "greater-than-equals": ">=",
  "less-than": "<",
  "less-than-equals": "<=",
};

const getLiteralText = (expression: LiteralExpression) => expression.text ?? String(expression.value);

/**
 * For a comparison between an attribute with a declared type and a static value (e.g. the leave taken code = "502"),
 * returns the attribute so the value can be converted to its type. Reports text attributes compared using an
 * ordering operator, which the rules engine can't evaluate (it only orders numbers and dates).
 */
const getComparedAttribute = (
  context: CompilerContext,
  scope: CompilerScope,
  expression: CommandExpression,
): Attribute | undefined => {
  if (!COMPARISON_COMMANDS.has(expression.command.id) || expression.arguments.length !== 2) {
    return undefined;
  }
  const attributeExpression = expression.arguments.find(
    (argument): argument is AttributeExpression => argument.type === "attribute",
  );
  const literalExpression = expression.arguments.find(
    (argument): argument is LiteralExpression => argument.type === "literal",
  );
  if (!attributeExpression || !literalExpression || literalExpression.value === null) {
    return undefined;
  }
  const attribute = scope.getAttributeById(attributeExpression.attributeId);
  const declaredType = getDeclaredType(attribute?.type);
  if (!attribute || !declaredType) {
    return undefined;
  }
  const symbol = ORDERING_SYMBOLS[expression.command.id];
  if (symbol && declaredType === "text") {
    addError(context.errors, {
      type: COMPILER_ERROR_TYPES.INVALID_ARGUMENT.id,
      message: `Invalid comparison '${attribute.description} ${symbol} ${getLiteralText(literalExpression)}': '${attribute.description}' is text, which can only be compared using = or <>`,
    });
    return undefined;
  }
  return attribute;
};

/**
 * Converts a static value to the declared type of the attribute it is compared with or assigned to, reporting values
 * that aren't valid for the type. Values for attributes with no declared type are left as parsed.
 */
const toDeclaredValue = (context: CompilerContext, expression: LiteralExpression, attribute: Attribute | undefined) => {
  if (!attribute) {
    return expression.value;
  }
  const text = getLiteralText(expression);
  const result = parseDeclaredValue(text, attribute.type);
  if (!result) {
    return expression.value;
  }
  if ("expected" in result) {
    addError(context.errors, {
      type: COMPILER_ERROR_TYPES.INVALID_ARGUMENT.id,
      message: `Invalid value '${text}' for '${attribute.description}': expected ${result.expected}`,
    });
    return expression.value;
  }
  return result.value;
};

export const convertExpressionsToElements = (
  context: CompilerContext,
  scope: CompilerScope,
  expressions: Expression[],
  convertContext: ExpressionConvertContext,
) => {
  const result: ExpressionsConvertResult = {
    elements: [],
    connector: undefined,
    expressionMap: convertContext?.expressionMap ?? new WeakMap(),
    scopedEntity: convertContext.scopedEntity,
  };

  if (expressions.some((arg) => arg.alias)) {
    convertContext.relationshipEntity = "global";
  }

  if (!convertContext.relationshipEntity) {
    if (convertContext.attributeAssignment?.inferred?.type === "targeted") {
      convertContext.relationshipEntity = findContainmentRelationship(
        context.relationships,
        convertContext.attributeAssignment.inferred.target,
      )?.name;
    }
  }

  for (const expression of expressions) {
    if (expression.operator) {
      result.connector = expression.operator;
    }
    if (expression.type === "command") {
      if (expression.command.id === "current-date") {
        const element: NodeElement = {
          type: "value",
          value: null,
          special: "CurrentDate",
        };
        result.elements.push(element);
        result.expressionMap.set(element, expression);
        continue;
      }

      if (expression.command.id === "latest") {
        const element: NodeElement = {
          type: "value",
          value: null,
          special: "EndOfTime",
        };
        result.elements.push(element);
        result.expressionMap.set(element, expression);
        continue;
      }

      if (expression.command.id === "earliest") {
        const element: NodeElement = {
          type: "value",
          value: null,
          special: "BeginningOfTime",
        };
        result.elements.push(element);
        result.expressionMap.set(element, expression);
        continue;
      }

      const relationshipId = expression.relationshipId;

      let relationshipReverse: boolean | undefined = expression.relationshipReverse;
      let entityScope: Scope | undefined = relationshipId ? "global" : undefined;


      if (relationshipId) {
        if (relationshipReverse) {
          const containment = findContainmentRelationship(context.relationships, relationshipId);
          if (containment?.name) {
            entityScope = {entity: containment.name};
          }
        } else {
          const relationship = context.relationships.find((relationship) => relationship.id === relationshipId);

          const attributeEntity = convertContext?.scopedEntity || convertContext?.attributeAssignment?.attribute.entity;
          const {relationship: scopedRelationship, reverse} = getScopedRelationship(
            context.relationships,
            attributeEntity,
            relationship,
            // TODO: must be a better way of expressing this logic -- just not sure what the exact rule is
            true,
          );
          //console.log(relationship, "->", convertContext?.assignableAttribute, "=", scopedRelationship, ",", reverse)

          relationshipReverse = reverse;
          //entityScope = typeof attributeEntity === "string" && attributeEntity !== "global" ? { entity: attributeEntity } : "global";
          if (typeof attributeEntity === "string" && attributeEntity !== "global") {
            let sourceRelationship = context.relationships.find(
              (other) => other.target === scopedRelationship?.source && other.id !== scopedRelationship?.id,
            );
            let previousRelationship = sourceRelationship;

            let attributeRelationship = context.relationships.find((other) => other.name === attributeEntity);
            const attributeEntities: string[] = [];
            while (attributeRelationship?.source) {
              if (attributeRelationship.name) {
                attributeEntities.push(attributeRelationship.name);
              }
              attributeRelationship = context.relationships.find(
                (other) => other.target === attributeRelationship?.source && other.id !== attributeRelationship?.id,
              );
            }

            while (
              sourceRelationship &&
              (!sourceRelationship?.containment ||
                !sourceRelationship.name ||
                !attributeEntities.includes(sourceRelationship.name) ||
                sourceRelationship?.id === scopedRelationship?.id)
              ) {
              sourceRelationship = context.relationships.find(
                (other) => other.target === sourceRelationship?.source && other.id !== scopedRelationship?.id,
              );
              if (!sourceRelationship) {
                break;
              }
              if (previousRelationship?.id === sourceRelationship?.id) {
                break;
              }
              previousRelationship = sourceRelationship;
            }

            if (sourceRelationship?.name !== attributeEntity || expression.command.id.endsWith("-scope")) {
              entityScope = sourceRelationship?.name ? {entity: sourceRelationship.name} : "global";
            }
          }
        }
      }

      const scopedToEntity = relationshipId && findContainmentRelationship(context.relationships, relationshipId)?.name;

      if (expression.command.id.endsWith("-scope")) {
        result.scopedEntity = scopedToEntity;
      }

      const inFor = expression.command.id === "for";

      const argumentsResult = convertExpressionsToElements(context, scope, expression.arguments, {
        ...convertContext,
        expressionMap: result.expressionMap,
        relationshipEntity: scopedToEntity || convertContext?.relationshipEntity,
        inFor: inFor,
        declaredValueFor: getComparedAttribute(context, scope, expression),
      });

      /*if (argumentsResult.relationshipId) {
        const relationship = context.relationships.find((relationship) => relationship.id === argumentsResult.relationshipId);
        if (assignableAttribute && relationship?.name !== assignableAttribute.entity) {
          scope = {
            entity: assignableAttribute.entity,
          };
        }
      }*/

      const element: NodeElement = {
        type: expression.command.id,
        scope: entityScope,
        relationshipId,
        relationshipReverse: relationshipReverse,
        elements: expression.command.id === ORDINAL_COMMAND.id ? [] : argumentsResult?.elements || [],
      };

      if (expression.command.id === ORDINAL_COMMAND.id) {
        const [argumentElement] = argumentsResult?.elements ?? [];
        if (argumentElement?.type === "for") {
          const scopedOrdinalElement: NodeElement = {
            ...argumentElement,
            attributeId: undefined,
            elements: [element],
          };
          result.elements.push(scopedOrdinalElement);
          result.expressionMap.set(scopedOrdinalElement, expression);
          continue;
        }
      }

      // explicit for
      if (element.type === "for") {
        if (element.elements[0]?.type === "attribute") {
          element.attributeId = (element.elements[0] as any).attributeId;
          element.elements = element.elements.slice(1);
        }


        if (element.attributeId) {
          const forElement = element as AttributeElement;
          const attribute = scope.getAttributeById(forElement.attributeId);

          if (attribute) {
            const currentlyScopedTo = convertContext?.relationshipEntity || convertContext?.attributeAssignment?.attribute.entity;
            const relationship = context.relationships.find((relationship) => relationship.id === forElement.relationshipId);
            const fromRelationship = currentlyScopedTo ? context.relationships.find((relationship) => relationship.name === currentlyScopedTo) : undefined;
            const toRelationship = attribute?.entity ? context.relationships.find((relationship) => relationship.name === attribute?.entity) : undefined;

            let badScoping = false;


            if (relationship?.reverseText) {
              badScoping = (relationship?.target || "global") !== (fromRelationship?.target || "global") ||
                (relationship?.source || "global") !== (toRelationship?.target || "global");
            } else {
              badScoping = (relationship?.source || "global") !== (fromRelationship?.target || "global") ||
                (relationship?.target || "global") !== (toRelationship?.target || "global");
            }

            if (badScoping) {
              context.errors = addError(context.errors, {
                type: COMPILER_ERROR_TYPES.INVALID_SCOPE.id,
                message: `Attribute '${attribute.description}' is not in the correct scope, unknown instance of '${toRelationship?.textSingular ?? attribute.entity}' being referenced from '${fromRelationship?.textSingular ?? currentlyScopedTo}'`,
              });
            }

            /*if (!sameAsBase) {
              forElement.relationshipId = relationship?.id;
              forElement.relationshipReverse = reverse;
            }
            forElement.scope = attribute.entity === "global" ? "global" : { entity: attribute.entity };*/
          }
        }
      }

      result.elements.push(element);
      result.expressionMap.set(element, expression);
    } else if (expression.type === "relationship") {
      //result.relationshipId = expression.relationshipId;
    } else if (expression.type === "literal") {
      const value =
        expression.value === null ? null : toDeclaredValue(context, expression, convertContext.declaredValueFor);
      if (value === null) {
        const element: NodeElement = {
          type: "uncertain",
        };
        result.elements.push(element);
        result.expressionMap.set(element, expression);
      } else if (typeof value === "number" && value < 0 && !expression.quoted) {
        const element: NodeElement = {
          type: "negate",
          elements: [
            {
              type: "value",
              value: Math.abs(value),
            },
          ],
        };
        result.elements.push(element);
        result.expressionMap.set(element, expression);
      } else {
        const element: NodeElement = {
          type: "value",
          value: value,
        };
        result.elements.push(element);
        result.expressionMap.set(element, expression);
      }
    } else if (expression.type === "attribute") {
      const attribute = scope.getAttributeById(expression.attributeId);
      if (!attribute) {
        throw new Error(`Attribute ${expression.attributeId} not found`);
      }
      const currentlyScopedTo = convertContext?.relationshipEntity || convertContext?.attributeAssignment?.attribute.entity;


      if (
        !expression.alias &&
        attribute.entity !== (currentlyScopedTo ?? "global") &&
        !convertContext?.inFor
      ) {
        const {relationship, reverse} = getScopedRelationship(
          context.relationships,
          currentlyScopedTo,
          attribute?.entity,
        );


        let sameAsBase = false;
        if (
          attribute.entity !== "global" &&
          relationship?.id !== convertContext?.attributeAssignment?.inferred?.rel
        ) {
          const checkScoping = (scope: string | undefined) => {
            if (!scope) {
              return undefined;
            }

            if (attribute.entity === scope) {
              sameAsBase = true;
            } else {
              const assignableEntity = scope;
              if (
                !assignableEntity ||
                !getRelationshipChain(context.relationships, assignableEntity)?.includes(attribute.entity)
              ) {
                return context.relationships.find((relationship) => relationship.name === scope) ?? {
                  textSingular: "global"
                };
              }
            }

            return false;
          };

          const collectScopingErrors = (...results: any[]) => {
            let hasGoodScope = false;
            let badRelationship;
            for (const result of results) {
              if (result === undefined) {
                continue;
              }
              if (result === false) {
                hasGoodScope = true;
              }
              if (result) {
                badRelationship = result;
              }
            }

            if (hasGoodScope) {
              return undefined;
            }
            return badRelationship;
          };

          const baseScopeCheck = checkScoping(convertContext?.attributeAssignment?.attribute?.entity);
          const scopedEntityCheck = checkScoping(convertContext?.scopedEntity);
          let badScoping = collectScopingErrors(baseScopeCheck, scopedEntityCheck);
          if (badScoping) {
            const attributeRelationship = context.relationships.find((relationship) => relationship.name === attribute.entity);
            context.errors = addError(context.errors, {
              type: COMPILER_ERROR_TYPES.INVALID_SCOPE.id,
              message: `Attribute '${attribute.description}' is not in the correct scope, unknown instance of '${attributeRelationship?.textSingular ?? attribute.entity}' being referenced from '${badScoping.textSingular}'`,
            });
          }
        }

        const element: NodeElement = {
          type: "for",
          scope: attribute.entity === "global" ? "global" : {entity: attribute.entity},
          attributeId: expression.attributeId,
          ...(sameAsBase
            ? {}
            : {
              relationshipId: relationship?.id,
              relationshipReverse: reverse,
            }),
        };


        result.elements.push(element);
        result.expressionMap.set(element, expression);
      } else {
        const element: NodeElement = {
          type: "attribute",
          attributeId: expression.attributeId,
        };
        result.elements.push(element);
        result.expressionMap.set(element, expression);
      }
    }
  }
  return result;
};
